import { useEffect, useState } from "react";
import { api } from "@/api/client";
import { BellRing, AlertTriangle, X, Clock } from "lucide-react";

/**
 * Global PM reminder bell — shown in the app header on every page.
 * Polls /api/pm/reminders every 5 minutes and shows a badge with count.
 * Clicking opens a modal listing overdue + upcoming PMs, each with a "Snooze 7d" action.
 */
export default function PMReminderBell() {
  const [reminders, setReminders] = useState([]);
  const [open, setOpen] = useState(false);
  const [snoozing, setSnoozing] = useState(null); // id being snoozed
  const [snoozeTarget, setSnoozeTarget] = useState(null); // { id, days }

  const load = async () => {
    try {
      const r = await api.get("/pm/reminders?within_days=7");
      setReminders(r.data || []);
    } catch { /* silent — non-fatal */ }
  };

  useEffect(() => {
    load();
    const interval = setInterval(load, 5 * 60 * 1000); // refresh every 5 min
    return () => clearInterval(interval);
  }, []);

  const snooze = async (id, days = 7, reason = "") => {
    setSnoozing(id);
    try {
      const qs = reason.trim() ? `?days=${days}&reason=${encodeURIComponent(reason.trim())}` : `?days=${days}`;
      await api.post(`/pm/${id}/snooze${qs}`);
      await load();
    } catch { /* non-fatal */ }
    finally { setSnoozing(null); setSnoozeTarget(null); }
  };

  if (reminders.length === 0) return null;
  const overdue = reminders.filter((r) => r.overdue);
  const isUrgent = overdue.length > 0;

  return (
    <>
      <button
        data-testid="pm-bell"
        onClick={() => setOpen(true)}
        title={`${reminders.length} PM${reminders.length !== 1 ? "s" : ""} need attention`}
        className={`relative inline-flex items-center justify-center rounded-md border p-2 transition ${isUrgent ? "border-rose-500 bg-rose-500/10 text-rose-600 hover:bg-rose-500/20" : "border-amber-500 bg-amber-500/10 text-amber-600 hover:bg-amber-500/20"}`}
      >
        <BellRing className={`h-4 w-4 ${isUrgent ? "animate-pulse" : ""}`} />
        <span className="absolute -top-1.5 -right-1.5 min-w-[18px] rounded-full bg-current px-1 py-px text-center text-[10px] font-bold leading-tight text-white">
          {reminders.length}
        </span>
      </button>

      {open && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4" onClick={() => setOpen(false)}>
          <div className="w-full max-w-lg sharp-card max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between border-b border-border/60 px-5 py-3">
              <div className="flex items-start gap-2">
                <BellRing className="mt-0.5 h-5 w-5 text-amber-500" />
                <div>
                  <p className="label-eyebrow text-amber-500">PM Reminder</p>
                  <h3 className="mt-0.5 font-heading text-lg font-semibold">Visits need attention</h3>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {overdue.length > 0 && <><b className="text-rose-600">{overdue.length} overdue</b> · </>}
                    <span>{reminders.length - overdue.length} due within 7 days</span>
                  </p>
                </div>
              </div>
              <button data-testid="pm-bell-close" onClick={() => setOpen(false)} className="rounded-md border border-border/70 p-1.5">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-1.5">
              {reminders.slice(0, 30).map((r) => (
                <div key={r.id} className={`flex items-start justify-between gap-2 rounded-md border px-3 py-2 text-xs ${r.overdue ? "border-rose-500/40 bg-rose-500/5" : "border-border/60"}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      {r.overdue && <AlertTriangle className="h-3 w-3 text-rose-500" />}
                      <span className="font-medium text-foreground">{r.customer_name || "Unknown"}</span>
                      {r.quarter && <span className="rounded-sm bg-primary/10 px-1.5 py-0.5 text-[10px] font-bold uppercase text-primary">{r.quarter}</span>}
                    </div>
                    <div className="mt-0.5 text-[11px] text-muted-foreground">
                      Due <b className={r.overdue ? "text-rose-600" : ""}>{r.due_date}</b>
                      {r.po_no && <> · PO <span className="font-mono">{r.po_no}</span></>}
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5">
                    <Clock className="mr-1 h-3 w-3 text-muted-foreground" />
                    {[3, 7, 14, 30].map((n) => (
                      <button
                        key={n}
                        data-testid={`pm-bell-snooze-${r.id}-${n}`}
                        onClick={() => setSnoozeTarget({ id: r.id, days: n, customer: r.customer_name })}
                        disabled={snoozing === r.id}
                        title={`Push due date +${n} days`}
                        className="rounded-md border border-border/70 bg-card px-1.5 py-1 text-[10px] font-semibold text-muted-foreground hover:border-primary hover:text-primary disabled:opacity-60"
                      >
                        {snoozing === r.id ? "…" : `${n}d`}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              {reminders.length > 30 && <p className="pt-1 text-center text-[11px] text-muted-foreground">…and {reminders.length - 30} more</p>}
            </div>
            <div className="border-t border-border/60 px-5 py-3 flex justify-end gap-2">
              <button
                onClick={() => setOpen(false)}
                className="rounded-md border border-border/70 px-4 py-2 text-xs font-medium"
              >
                Close
              </button>
              <a
                href="/app/pm"
                onClick={() => setOpen(false)}
                data-testid="pm-bell-goto"
                className="rounded-md bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground hover:bg-primary/90"
              >
                Open PM Schedule
              </a>
            </div>
          </div>
        </div>
      )}

      {snoozeTarget && (
        <SnoozeReasonModal
          target={snoozeTarget}
          onClose={() => setSnoozeTarget(null)}
          onConfirm={(reason) => snooze(snoozeTarget.id, snoozeTarget.days, reason)}
        />
      )}
    </>
  );
}

function SnoozeReasonModal({ target, onClose, onConfirm }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const PRESETS = ["Parts on order", "Customer request", "Engineer unavailable", "Site not ready"];
  const submit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) return;
    setBusy(true);
    await onConfirm(reason);
  };
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <form onSubmit={submit} className="w-full max-w-md sharp-card" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border/60 px-5 py-3">
          <div>
            <p className="label-eyebrow text-primary">Snooze PM</p>
            <h3 className="mt-1 font-heading text-base font-semibold">
              Push <span className="text-primary">{target.customer || "PM"}</span> +{target.days} day{target.days !== 1 ? "s" : ""}?
            </h3>
          </div>
          <button type="button" data-testid="pm-snooze-cancel" onClick={onClose} className="rounded-md border border-border/70 p-1.5">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-3 p-5">
          <p className="text-xs text-muted-foreground">Why are you snoozing this PM? (Logged in the audit trail.)</p>
          <div className="flex flex-wrap gap-1.5">
            {PRESETS.map((p) => (
              <button
                type="button"
                key={p}
                data-testid={`pm-snooze-chip-${p.toLowerCase().replace(/ /g, "-")}`}
                onClick={() => setReason(p)}
                className={`rounded-full border px-3 py-1 text-[11px] font-medium transition ${reason === p ? "border-primary bg-primary/10 text-primary" : "border-border/70 text-muted-foreground hover:bg-secondary"}`}
              >
                {p}
              </button>
            ))}
          </div>
          <textarea
            data-testid="pm-snooze-reason"
            required
            autoFocus
            rows={2}
            maxLength={200}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Tap a chip or write your own reason…"
            className="w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="rounded-md border border-border/70 px-4 py-2 text-sm">Cancel</button>
            <button data-testid="pm-snooze-confirm" type="submit" disabled={!reason.trim() || busy} className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
              {busy ? "Snoozing…" : `Snooze +${target.days}d`}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
